import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import addonAssignmentService, { ADDON_DISPLAY_NAMES, type AddonAccessRequest } from '../../services/addonAssignmentService';
import { subscriptionChipSx } from '../../utils/subscriptionStatus';
import { getApiErrorInfo } from '../../services/api';
import tenantService from '../../services/tenantService';
import { useUserContext } from '../../hooks/useUserContext';

interface AccessRequestsPanelProps {
  embedded?: boolean;
  onApprovalSuccess?: () => Promise<void>;
}

export default function AccessRequestsPanel({ embedded = false, onApprovalSuccess }: AccessRequestsPanelProps) {
  const { isAdmin: isSystemAdmin } = useUserContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTenantId, setCurrentTenantId] = useState<string | null>(null);
  const [requests, setRequests] = useState<AddonAccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'pending' | 'approved' | 'denied'>('pending');
  const [error, setError] = useState<string | null>(null);

  // Review dialog
  const [reviewRequest, setReviewRequest] = useState<AddonAccessRequest | null>(null);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'denied'>('approved');
  const [reviewNote, setReviewNote] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  useEffect(() => {
    initializeTenant();
  }, []);

  useEffect(() => {
    if (currentTenantId) fetchRequests();
  }, [currentTenantId, tab]);

  const initializeTenant = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const queryTenantId = urlParams.get('tenantId');
      if (queryTenantId && isSystemAdmin) {
        setCurrentTenantId(queryTenantId);
        return;
      }
      const tenantId = await tenantService.determineTenantId();
      setCurrentTenantId(tenantId);
    } catch {
      setError('Unable to load your organization. Please refresh the page.');
    }
  };

  const fetchRequests = async () => {
    if (!currentTenantId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await addonAssignmentService.getAccessRequests(currentTenantId, tab);
      setRequests(data);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to fetch access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReview = (req: AddonAccessRequest, decision: 'approved' | 'denied') => {
    setReviewRequest(req);
    setReviewDecision(decision);
    setReviewNote('');
  };

  const handleSubmitReview = async () => {
    if (!reviewRequest || !currentTenantId) return;
    try {
      setReviewLoading(true);
      await addonAssignmentService.reviewRequest(
        currentTenantId,
        reviewRequest.id,
        reviewDecision,
        reviewNote.trim() || undefined
      );
      setReviewRequest(null);
      await fetchRequests();
      if (reviewDecision === 'approved' && onApprovalSuccess) {
        await onApprovalSuccess();
      }
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to submit review');
      setReviewRequest(null);
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Module Access Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and approve user requests for module access
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Pending" value="pending" />
        <Tab label="Approved" value="approved" />
        <Tab label="Denied" value="denied" />
      </Tabs>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Status</TableCell>
              {tab === 'pending' && <TableCell align="right">Actions</TableCell>}
              {tab !== 'pending' && <TableCell>Review Note</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography color="text.secondary">
                    No {tab} requests
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              requests.map(req => (
                <TableRow key={req.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{req.userFullName}</Typography>
                    <Typography variant="caption" color="text.secondary">{req.userEmail}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={ADDON_DISPLAY_NAMES[req.addonId] ?? req.addonId} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(req.requestedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {req.requestNote || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={req.status}
                      size="small"
                      sx={[
                        subscriptionChipSx(
                          req.status === 'approved' ? 'success' : req.status === 'denied' ? 'error' : 'warning'
                        ),
                        { textTransform: 'capitalize' },
                      ]}
                    />
                  </TableCell>
                  {tab === 'pending' && (
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleOpenReview(req, 'approved')}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleOpenReview(req, 'denied')}
                        >
                          Deny
                        </Button>
                      </Box>
                    </TableCell>
                  )}
                  {tab !== 'pending' && (
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {req.reviewNote || '—'}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Review Dialog */}
      <Dialog open={!!reviewRequest} onClose={() => setReviewRequest(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" component="span">
            {reviewDecision === 'approved' ? 'Approve' : 'Deny'} Access Request
          </Typography>
          <IconButton onClick={() => setReviewRequest(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {reviewRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                <strong>{reviewRequest.userFullName}</strong> requested access to{' '}
                <strong>{ADDON_DISPLAY_NAMES[reviewRequest.addonId] ?? reviewRequest.addonId}</strong>.
              </Typography>
              {reviewRequest.requestNote && (
                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'tint.neutralSoft' }}>
                  <Typography variant="body2" color="text.secondary">
                    User note: {reviewRequest.requestNote}
                  </Typography>
                </Box>
              )}
              <TextField
                label={reviewDecision === 'denied' ? 'Reason for denying (optional)' : 'Note to user (optional)'}
                helperText={
                  reviewDecision === 'denied'
                    ? 'Shared with the user so they know why the request was denied.'
                    : 'Shared with the user alongside the decision.'
                }
                multiline
                minRows={2}
                fullWidth
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                disabled={reviewLoading}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setReviewRequest(null)} variant="outlined" disabled={reviewLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            color={reviewDecision === 'approved' ? 'success' : 'error'}
            disabled={reviewLoading}
          >
            {reviewLoading ? <CircularProgress size={20} /> : reviewDecision === 'approved' ? 'Approve' : 'Deny'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return embedded ? content : <Container maxWidth="xl" sx={{ py: 4 }}>{content}</Container>;
}
